<template>
	<Modal title="Take Action" modal-width="half-width">
		<div>
			<div class="modal-header bg-primary text-white">
				<h4 class="modal-title text-white mt-2">
					<i class="fas fa-landmark me-2"></i>
					{{
						action === "approve"
							? "Application Recommendation"
							: "Reject Application"
					}}
				</h4>
			</div>

			<div class="alert alert-warning mt-2" style="text-align: left">
				<strong>
					Note: This recommendation indicates that the application has
					successfully passed the initial administrative review and is now
					cleared to proceed to the administrative and physical verification
					phase.<br /><br />
					At this stage, the application can either be approved, allowing
					further verification, or rejected, in which case the application is
					declined with no further processing.
				</strong>
			</div>

			<div style="text-align: left">
				<div class="action-tabs mb-4">
					<ul class="nav nav-pills nav-fill">
						<li class="nav-item">
							<a
								class="nav-link"
								:class="{ active: action === 'approve' }"
								@click="setAction('approve')"
							>
								<i class="fas fa-check-circle me-2"></i>Recommendation
							</a>
						</li>
						<li class="nav-item">
							<a
								class="nav-link"
								:class="{ active: action === 'reject' }"
								@click="setAction('reject')"
							>
								<i class="fas fa-times-circle me-2"></i>Reject
							</a>
						</li>
					</ul>
				</div>

				<div v-if="action === 'reject'">
					<div class="form-group">
						<label for="rejectRemarks" class="font-weight-bold">
							<i class="fas fa-comment-dots me-2"></i>Reason for rejection
							(Required)
						</label>
						<textarea
							class="form-control"
							id="rejectRemarks"
							rows="4"
							v-model="comment"
							placeholder="Please provide detailed reasons for rejection..."
							required
						></textarea>
						<small class="text-muted"
							>The message in this box will be sent to the association.</small
						>
					</div>
				</div>

				<div v-if="action === 'approve'">
					<div class="form-group">
						<label for="approvalComment" class="font-weight-bold">
							<i class="fas fa-comment me-2"></i>Remarks (Optional)
						</label>
						<textarea
							class="form-control"
							id="approvalComment"
							rows="3"
							v-model="comment"
							placeholder="Any additional remarks for the recommendation..."
						></textarea>
					</div>
				</div>

				<div v-if="isError" class="alert alert-danger">
					<strong>Failed to update application, please try again</strong>
				</div>
			</div>

			<div class="modal-footer">
				<button
					type="button"
					class="btn btn-outline-secondary"
					@click="hideModal"
					:disabled="isLoading"
				>
					<i class="fas fa-times me-2"></i>Cancel
				</button>
				<button
					type="button"
					class="btn"
					:class="action === 'approve' ? 'btn-primary' : 'btn-danger'"
					@click.prevent="submitAction"
					:disabled="isLoading"
				>
					<i
						class="fas"
						:class="action === 'approve' ? 'fa-check' : 'fa-ban'"
					></i>
					<span v-if="isLoading">Please wait...</span>
					<span v-else>{{
						action === "approve"
							? "Recommend Application"
							: "Reject Application"
					}}</span>
				</button>
			</div>
		</div>
	</Modal>
</template>

<script>
import Modal from "@/components/Modal.vue";
import { EventBus } from "@/utils/event_bus.js";
import Swal from "sweetalert2";

export default {
	components: {
		Modal,
	},
	data() {
		return {
			action: "approve",
			section: "provisional_approval",
			comment: "",
			isLoading: false,
			isError: false,
		};
	},
	methods: {
		setAction(action) {
			this.comment = "";
			this.action = action;
			this.isError = false;
		},

		submitAction() {
			if (this.action == "rejected" && this.comment == null) {
				return Swal.fire({
					text: "Remark must be provided while rejecting application",
					icon: "error",
				});
			}

			let payload = {
				partyID: this.$route.params.id,
				section: this.section,
				comment: this.comment,
				status: this.action == "approve" ? "approved" : "rejected",
			};

			this.hideModal();
			this.isLoading = true;
			this.isError = false;

			let title =
				this.action == "approve"
					? "You're about to recommend this application?"
					: "Are you want to reject this application?";

			let actionMsg =
				this.action == "approve" ? "Yes, Recommend" : "Yes, Reject";

			Swal.fire({
				title: title,
				text: "You won't be able to revert this!",
				icon: "question",
				showCancelButton: true,
				confirmButtonColor: "#4680ff",
				cancelButtonColor: "#d33",
				confirmButtonText: actionMsg,
				reverseButtons: true,
			}).then((result) => {
				if (result.isConfirmed) {
					this.showModal();
					this.$store
						.dispatch("parties/approveApplicationSection", payload)
						.then((resp) => {
							if (resp.status_code === 200) {
								this.comment = null;
								Swal.fire({
									icon: "success",
									title: "Recommendation Saved",
									// text: "Action saved!",
								});
							} else {
								Swal.fire({
									icon: "error",
									title: "Error",
									text:
										resp.response.data.errors.message ??
										"Unable to complete action",
								});
							}
							this.isLoading = false;
							this.hideModal();
						})
						.catch(() => {
							this.isLoading = false;
							this.isError = true;
						});
				} else {
					this.isLoading = false;
					this.showModal();
				}
			});
		},

		resetForm() {
			this.section = "";
			this.comment = "";
			this.action = "approve";
		},

		showModal() {
			EventBus.$emit("toggle-modal", "show");
		},

		hideModal() {
			EventBus.$emit("toggle-modal", "hide");
		},
	},
};
</script>

<style scoped>
.modal-header {
	border-radius: 0;
	padding: 2px 20px;
}

.modal-title {
	font-weight: 600;
}

.action-tabs .nav-link {
	cursor: pointer;
	transition: all 0.3s;
	border-radius: 0.25rem;
}

.action-tabs .nav-link.active {
	background-color: #2c3e50;
	color: white;
}

.action-tabs .nav-link:not(.active) {
	background-color: #f8f9fa;
	color: #495057;
}

.action-tabs .nav-link:not(.active):hover {
	background-color: #e9ecef;
}

.custom-file-label::after {
	content: "Browse";
}

.btn-primary {
	background-color: #4680ff;
	border-color: #4680ff;
}

.btn-success {
	background-color: #28a745;
	border-color: #28a745;
}

.btn-danger {
	background-color: #dc3545;
	border-color: #dc3545;
}

textarea.form-control {
	min-height: 100px;
}
</style>
